/**
 * Agent Findings Service
 *
 * DB read/write for agent_findings — the reviewable analytical artifact emitted
 * by the intelligence agents (cassandra, compass, keystone).
 *
 * A finding groups one or more items with a severity, a narrative, and
 * structured evidence. Humans accept or dismiss findings on the agent's report
 * page, exactly like Twins clusters.
 */

import { getSupabaseClient } from './supabaseClient';

const TABLE = 'agent_findings';

// ── Types ─────────────────────────────────────────────────────────────────────

export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type FindingStatus = 'active' | 'accepted' | 'dismissed';

export interface AgentFinding {
  id: string;
  workspaceId: string;
  agentType: string;
  /** Agent-specific code, e.g. 'knife_edge', 'orphaned_objective', 'keystone'. */
  findingType: string;
  severity: FindingSeverity;
  title: string;
  summary: string;
  detail: string;
  itemIds: string[];
  data: Record<string, unknown>;
  status: FindingStatus;
  proposedByAgent: string;
  createdAt: string;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
}

/** Shape an analyzer emits; the service fills in workspace + agent. */
export interface NewFinding {
  findingType: string;
  severity: FindingSeverity;
  title: string;
  summary: string;
  detail: string;
  itemIds: string[];
  data?: Record<string, unknown>;
}

// ── Converters ────────────────────────────────────────────────────────────────

function rowToFinding(row: Record<string, unknown>): AgentFinding {
  const rawIds = row.item_ids;
  const itemIds: string[] = Array.isArray(rawIds)
    ? (rawIds as string[])
    : typeof rawIds === 'string'
      ? (JSON.parse(rawIds) as string[])
      : [];

  const rawData = row.data;
  const data: Record<string, unknown> =
    rawData && typeof rawData === 'object'
      ? (rawData as Record<string, unknown>)
      : typeof rawData === 'string'
        ? (JSON.parse(rawData) as Record<string, unknown>)
        : {};

  return {
    id:               row.id as string,
    workspaceId:      row.workspace_id as string,
    agentType:        row.agent_type as string,
    findingType:      row.finding_type as string,
    severity:         (row.severity as FindingSeverity) ?? 'medium',
    title:            (row.title as string) ?? '',
    summary:          (row.summary as string) ?? '',
    detail:           (row.detail as string) ?? '',
    itemIds,
    data,
    status:           (row.status as FindingStatus) ?? 'active',
    proposedByAgent:  (row.proposed_by_agent as string) ?? '',
    createdAt:        row.created_at as string,
    reviewedAt:       (row.reviewed_at as string | null) ?? null,
    reviewedByUserId: (row.reviewed_by_user_id as string | null) ?? null,
  };
}

// ── Queries ───────────────────────────────────────────────────────────────────

export async function getActiveFindings(
  workspaceId: string,
  agentType: string,
): Promise<{ data: AgentFinding[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('agent_type', agentType)
      .eq('status', 'active')
      // critical → info, then newest first within a severity
      .order('created_at', { ascending: false });

    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToFinding(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getFindingsHistory(
  workspaceId: string,
  agentType: string,
  limit = 50,
): Promise<{ data: AgentFinding[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('agent_type', agentType)
      .in('status', ['accepted', 'dismissed'])
      .order('reviewed_at', { ascending: false })
      .limit(limit);

    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToFinding(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/** Delete stale active findings before a fresh run. Returns count removed. */
export async function clearActiveFindings(
  workspaceId: string,
  agentType: string,
): Promise<{ count: number; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('clear_active_agent_findings', {
      p_workspace_id: workspaceId,
      p_agent_type:   agentType,
    });
    if (error) return { count: 0, error: error.message };
    return { count: (data as number) ?? 0, error: null };
  } catch (err) {
    return { count: 0, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function createFinding(
  workspaceId: string,
  agentType: string,
  finding: NewFinding,
): Promise<{ data: AgentFinding | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        workspace_id:      workspaceId,
        agent_type:        agentType,
        finding_type:      finding.findingType,
        severity:          finding.severity,
        title:             finding.title,
        summary:           finding.summary,
        detail:            finding.detail,
        item_ids:          finding.itemIds,
        data:              finding.data ?? {},
        proposed_by_agent: agentType,
      })
      .select('*')
      .single();

    if (error) return { data: null, error: error.message };
    const created = rowToFinding(data as Record<string, unknown>);
    // Every finding writes a decision record (spec §2 I), as the server runtime's do;
    // reviewing the finding stamps it (migration 943).
    const firstItem = finding.itemIds?.[0];
    if (firstItem) {
      const { error: recordError } = await supabase.rpc('record_agent_decision', {
        p_workspace_id: workspaceId,
        p_agent_type: agentType,
        p_object_id: firstItem,
        p_output_kind: 'finding',
        p_rationale: finding.summary,
        p_object_type: 'item',
        p_field: finding.findingType,
        p_output_id: created.id,
        p_proposed_value: { severity: finding.severity, title: finding.title },
        p_evidence: [{ kind: 'computed', data: finding.data ?? {} }],
        p_stack_version: 'find_issues/2',
      });
      if (recordError) console.warn('[findings] decision record not written:', recordError.message);
    }
    return { data: created, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function acceptFinding(findingId: string, userId: string): Promise<{
  data: AgentFinding | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('accept_agent_finding', {
      p_finding_id: findingId,
      p_user_id:    userId,
    });
    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: 'Finding not found or already reviewed' };
    return { data: rowToFinding(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function dismissFinding(findingId: string, userId: string): Promise<{
  data: AgentFinding | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('dismiss_agent_finding', {
      p_finding_id: findingId,
      p_user_id:    userId,
    });
    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: 'Finding not found or already reviewed' };
    return { data: rowToFinding(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
